import {pruneQuery} from '@github-ui/filter-query'
import {isCustomPropertiesKey} from '@github-ui/repos-filter/providers'
import type {PropertyConfiguration, PropertySource, RepositoryPropertyParameters} from '../types/rules-types'
import type {SuppliedFilterProvider} from '@github-ui/filter-core'

export function buildQueryForProperty(property: PropertyConfiguration, negationPrefix: string): string {
  const valueString = property.property_values.map(quoteIfNeeded).join(',')
  const queryKey = getPropertyQueryKey(property)
  return `${negationPrefix}${queryKey}:${valueString}`
}

const specialCharactersPattern = new RegExp(/[ ()',]/)
function quoteIfNeeded(value: string) {
  return specialCharactersPattern.test(value) ? `"${value}"` : value
}

export function buildQueryForAllProperties(properties: RepositoryPropertyParameters): string {
  const includeParts = properties.include.map(property => buildQueryForProperty(property, ''))
  const excludeParts = properties.exclude.map(property => buildQueryForProperty(property, '-'))

  return [...includeParts, ...excludeParts].join(' ')
}

export function queryToPropertyParameters(
  query: string,
  providers: SuppliedFilterProvider[],
  namespaces: string[],
): RepositoryPropertyParameters {
  const validTokens = pruneQuery(query, providers)

  const include: PropertyConfiguration[] = []
  const exclude: PropertyConfiguration[] = []

  for (const token of validTokens) {
    if (token.type === 'text') {
      continue
    }

    const property: PropertyConfiguration = {
      name: dropPropertyPrefix(token.key),
      source: getPropertySourceType(token.key, namespaces),
      property_values: token.values,
    }

    if (token.isNegated) {
      exclude.push(property)
    } else {
      include.push(property)
    }
  }

  return {include, exclude}
}

export function getPropertyQueryKey(property: PropertyConfiguration): string {
  // Custom properties are not saved with the 'props.' prefix required to build the query. External properties are
  // saved with the namespace prefix 'azure.environment', and system properties do not require a prefix.
  // Therefore, we add the prefix for custom properties only, and leave the others.
  return (property.source || 'custom') === 'custom' ? `props.${property.name}` : property.name
}

function dropPropertyPrefix(key: string) {
  if (isCustomPropertiesKey(key)) {
    const [, ...segments] = key.split('.')
    const name = segments.join('.')
    return `${name}`
  }

  return key
}

function getPropertySourceType(key: string, namespaces: string[]): PropertySource {
  if (isCustomPropertiesKey(key)) return 'custom'

  const [namespace, propertyName] = key.split('.')
  if (namespace && propertyName && namespaces.includes(namespace)) return 'external'

  return 'system'
}
